import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Shield } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { makeApiCall, getAuthHeaders } from '../utils/apiConfig';
import { useAuth } from '../contexts/AuthContext';

type PrivacyRequestType = 'access' | 'delete' | 'correct' | 'portability';

export const PrivacyPreferencesCard: React.FC = () => {
  const { t } = useTranslation() as { t: (key: string) => string };
  const { userProfile, refreshUserProfile } = useAuth();
  const queryClient = useQueryClient();

  const region = userProfile?.preferences?.locale?.region;
  const state = userProfile?.preferences?.locale?.state;
  const doNotSellOrShare = userProfile?.preferences?.privacy?.doNotSellOrShare ?? false;
  const limitSensitiveUse = userProfile?.preferences?.privacy?.limitSensitiveUse ?? false;

  // US states with CCPA-like privacy laws (opt-out of sale, limit sensitive use)
  const US_PRIVACY_STATES = ['CA', 'TX', 'FL', 'VA', 'UT'];
  const isCCPALikeState = region === 'US' && state && US_PRIVACY_STATES.includes(state);
  const isCalifornia = region === 'US' && state === 'CA';
  const isCanada = region === 'CA';

  const [gdprRequestType, setGdprRequestType] = useState<PrivacyRequestType>('access');

  const saveMutation = useMutation({
    mutationFn: async (updates: { doNotSellOrShare?: boolean; limitSensitiveUse?: boolean }) => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/privacy/preferences', {
        method: 'PUT',
        headers,
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to save privacy preferences');
      }

      return response.json();
    },
    onSuccess: () => {
      refreshUserProfile();
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
    },
  });

  const requestMutation = useMutation({
    mutationFn: async (type: PrivacyRequestType) => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/privacy/requests', {
        method: 'POST',
        headers,
        body: JSON.stringify({ type }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to submit request');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
    },
  });

  const handleDoNotSellChange = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    saveMutation.mutate({ doNotSellOrShare: checked });
  };

  const handleLimitSensitiveChange = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    saveMutation.mutate({ limitSensitiveUse: checked });
  };

  const handleSubmitGdprRequest = () => {
    requestMutation.mutate(gdprRequestType);
  };

  // Not in a region that gets privacy options
  if (!isCCPALikeState && !isCanada) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Shield />
            {t('settings.privacyPreferences')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('privacyLocation.setLocation')}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Shield />
          {t('settings.privacyPreferences')}
        </Typography>

        {isCCPALikeState && (
          <>
            <Typography variant="subtitle1" fontWeight="medium" sx={{ mt: 2 }}>
              {t('privacyLocation.ccpaHeading')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('privacyLocation.ccpaDescription')}
            </Typography>
            {!isCalifornia && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                {t('privacyLocation.otherStatesNote')}
              </Typography>
            )}
            <Divider sx={{ my: 2 }} />

            {saveMutation.isError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {saveMutation.error instanceof Error
                  ? saveMutation.error.message
                  : t('errors.generic')}
              </Alert>
            )}

            {saveMutation.isSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {t('messages.saveSuccess')}
              </Alert>
            )}

            <Stack spacing={2}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 2,
                  p: 2,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={doNotSellOrShare}
                        onChange={handleDoNotSellChange}
                        disabled={saveMutation.isPending}
                        color="primary"
                      />
                    }
                    label={t('settings.doNotSellOrShare')}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, ml: 5 }}>
                    {t('settings.doNotSellOrShareDescription')}
                  </Typography>
                </Box>
                {saveMutation.isPending && (
                  <CircularProgress size={24} sx={{ flexShrink: 0 }} />
                )}
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 2,
                  p: 2,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={limitSensitiveUse}
                        onChange={handleLimitSensitiveChange}
                        disabled={saveMutation.isPending}
                        color="primary"
                      />
                    }
                    label={t('settings.limitSensitiveUse')}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, ml: 5 }}>
                    {t('settings.limitSensitiveUseDescription')}
                  </Typography>
                </Box>
                {saveMutation.isPending && (
                  <CircularProgress size={24} sx={{ flexShrink: 0 }} />
                )}
              </Box>
            </Stack>
          </>
        )}

        {isCanada && (
          <>
            <Typography variant="subtitle1" fontWeight="medium" sx={{ mt: 2 }}>
              {t('privacyLocation.gdprHeading')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('privacyLocation.gdprDescription')}
            </Typography>
            <Divider sx={{ my: 2 }} />

            {requestMutation.isError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {requestMutation.error instanceof Error
                  ? requestMutation.error.message
                  : t('privacyLocation.requestError')}
              </Alert>
            )}

            {requestMutation.isSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {t('privacyLocation.requestSuccess')}
              </Alert>
            )}

            <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'flex-end' }}>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>{t('privacyLocation.requestType')}</InputLabel>
                <Select
                  value={gdprRequestType}
                  label={t('privacyLocation.requestType')}
                  onChange={(e) => setGdprRequestType(e.target.value as PrivacyRequestType)}
                >
                  <MenuItem value="access">{t('privacyRequests.access')}</MenuItem>
                  <MenuItem value="delete">{t('privacyRequests.delete')}</MenuItem>
                  <MenuItem value="correct">{t('privacyRequests.correct')}</MenuItem>
                  <MenuItem value="portability">{t('privacyRequests.portability')}</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained"
                onClick={handleSubmitGdprRequest}
                disabled={requestMutation.isPending}
              >
                {requestMutation.isPending ? (
                  <CircularProgress size={24} />
                ) : (
                  t('privacyLocation.submitRequest')
                )}
              </Button>
            </Stack>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default PrivacyPreferencesCard;
